import type { ICustomerRef, TCustomerType } from '@team-milva/lab-models/customer'
import type { CustomerAccount } from '@team-milva/lab-models/customerAccount'
import type { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import { resolveIsInternal } from '@team-milva/lab-models'

/**
 * The two account maps an order's owner is resolved from. A view builds them once per render
 * (from `customerAccountStore` / `insuranceCustomerAccountStore`) and passes them in, so this stays
 * a pure lookup with no store access.
 */
export interface IOrderOwnerLookups {
  customerAccountsById: Partial<Record<string, CustomerAccount>>
  insuranceCustomerAccountsById: Partial<Record<string, InsuranceCustomerAccount>>
}

export interface IResolvedOrderOwner {
  type: TCustomerType
  /**
   * The owning account's name — the business customer or the insurance customer. `undefined` for
   * a private order (its "name" is the ordering user's, which the caller already resolves) and for
   * an account that is not loaded / no longer exists.
   */
  name: string | undefined
  /** The account's *current* internal flag. Only business customers can be internal. */
  isInternal: boolean
  customerAccount: CustomerAccount | undefined
  insuranceCustomerAccount: InsuranceCustomerAccount | undefined
}

/**
 * Resolves who owns an order from its `customer` reference (MIL-402), across business and
 * insurance customers. Use this instead of `customerAccountsById[order.customerAccountID]` —
 * an insurance customer's own order has `customerAccountID === ''`, so that lookup finds nothing.
 */
export function resolveOrderOwner(
  order: { customer: ICustomerRef },
  lookups: IOrderOwnerLookups
): IResolvedOrderOwner {
  const { type, id } = order.customer
  if (type === 'business') {
    const customerAccount = id ? lookups.customerAccountsById[id] : undefined
    return {
      type,
      name: customerAccount?.name,
      isInternal: customerAccount ? resolveIsInternal(customerAccount.options) : false,
      customerAccount,
      insuranceCustomerAccount: undefined,
    }
  }
  if (type === 'insurance') {
    const insuranceCustomerAccount = id ? lookups.insuranceCustomerAccountsById[id] : undefined
    return {
      type,
      name: insuranceCustomerAccount?.name,
      isInternal: false,
      customerAccount: undefined,
      insuranceCustomerAccount,
    }
  }
  return {
    type,
    name: undefined,
    isInternal: false,
    customerAccount: undefined,
    insuranceCustomerAccount: undefined,
  }
}

/** Name-only version of `IOrderOwnerLookups`, for callers that only display the owner. */
export interface IOrderOwnerNameLookups {
  customerAccountsById: Partial<Record<string, { name: string }>>
  insuranceCustomerAccountsById: Partial<Record<string, { name: string }>>
}

/**
 * The owning account's name — business or insurance customer — or `undefined` for a private
 * order and for an account that is not loaded.
 */
export function resolveOrderOwnerName(
  order: { customer: ICustomerRef },
  lookups: IOrderOwnerNameLookups
): string | undefined {
  const { type, id } = order.customer
  if (!id) return undefined
  if (type === 'business') return lookups.customerAccountsById[id]?.name
  if (type === 'insurance') return lookups.insuranceCustomerAccountsById[id]?.name
  return undefined
}
